import React, { useRef } from 'react';

import { css } from '@emotion/css';
import { theme } from 'theme';

import { useTruncateText } from 'hooks/useTruncateText';

import { parseNlToArray, parseTextForHtmlCharacterEntities } from 'utils/formatting';

export interface ExpandableTextBlockProps {
  text: string | React.ReactChild | undefined;
  rows?: number;
  expandNlToBreaks?: boolean;
  key?: string;
}

export function ExpandableTextBlock({ text, rows = 2, expandNlToBreaks = false, key }: ExpandableTextBlockProps) {
  const textRef = useRef<HTMLSpanElement>(null);

  const { isTruncated, isExpanded, setIsExpanded } = useTruncateText({ ref: textRef });

  if (typeof text !== 'string') {
    return <>{text}</>;
  }

  /**
   * Only render the More/Less button if the text is cut off.
   */
  const MoreLessButton = () => (
    <span
      className={css`
        color: ${theme.colors.link[500]};
        cursor: pointer;
        display: inline-block;
      `}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {isExpanded ? 'Less' : 'More'}
    </span>
  );
  const parsedText = parseTextForHtmlCharacterEntities(text);
  return (
    <div key={key}>
      <div
        className={`
          expandable-text-block-parent
          ${css`
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: ${isExpanded ? undefined : rows};
            -webkit-box-orient: vertical;
          `}
        `}
      >
        <span
          ref={textRef}
          className={`
            expandable-text-block
            ${css`
              display: -webkit-box;
              -webkit-line-clamp: ${isExpanded ? undefined : rows};
              -webkit-box-orient: vertical;
            `}`}
        >
          {expandNlToBreaks
            ? parseNlToArray(parsedText).map((line, idx) => (
                <React.Fragment key={`${key}-line-${idx}`}>
                  {line}
                  <br />
                </React.Fragment>
              ))
            : parsedText}
        </span>
      </div>
      {isTruncated && (
        <div>
          <MoreLessButton />
        </div>
      )}
    </div>
  );
}
